import React from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import {
  TextField,
  Box,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
} from '@mui/material'
import keys from '@/utils/keys'
import { sentenceCase } from '@/utils'

import { FormStatusType, BonusType } from '@/enums'
import { MAX_BONUS_PRIORITY, MIN_BONUS_PRIORITY } from '@/constants'
import { ApplicationState } from '@/state/root.models'
import { StateSchema } from '@/hooks/form/models'

import BonusPeriodComponent from './bonus-period/index.component'

type Props = {
  handleOnChange: (name: string, value: any) => void
  state: StateSchema
}

const BonusDetailsComponent = ({ handleOnChange, state }: Props) => {
  const { t } = useTranslation()

  const { bonusFormMode } = useSelector(
    ({ bonusEngine }: ApplicationState) => bonusEngine,
  )

  let bonusTypes = []

  const EXCLUDED_BONUS: Array<keyof typeof BonusType> = ['BETSOFT_CASINO']

  bonusFormMode === FormStatusType.CREATE
    ? (bonusTypes = [
        ...keys(BonusType)
          .filter(
            (item) =>
              BonusType[item] !== BonusType.SPORTS_STANDARD &&
              !EXCLUDED_BONUS.includes(item),
          )
          .map((x) => ({
            index: BonusType[x],
            value: x,
          })),
      ].sort((a, b) => a.value.localeCompare(b.value)))
    : (bonusTypes = [
        ...keys(BonusType)
          .filter(
            (item) =>
              BonusType[item] === state.type.value &&
              !EXCLUDED_BONUS.includes(item),
          )
          .map((x) => ({
            index: BonusType[x],
            value: x,
          })),
      ].sort((a, b) => a.value.localeCompare(b.value)))

  // bonusFormMode === FormStatusType.CREATE
  //   ? (bonusTypes = [
  //       ...keys(BonusType)
  //         .filter((item) => BonusType[item] !== BonusType.SPORTS_STANDARD)
  //         .map((x) => {
  //           return {
  //             index: BonusType[x],
  //             value: x,
  //           }
  //         }),
  //     ].sort((a, b) => a.value.localeCompare(b.value)))
  //   : (bonusTypes = [
  //       ...keys(BonusType)
  //         .filter((item) => BonusType[item] === state.type.value)
  //         .map((x) => {
  //           return {
  //             index: BonusType[x],
  //             value: x,
  //           }
  //         }),
  //     ].sort((a, b) => a.value.localeCompare(b.value)))

  return (
    <>
      <Typography>{t('bonusStepOne')}</Typography>
      <Box my={2} maxWidth={400}>
        <TextField
          name="name"
          fullWidth
          margin="dense"
          variant="outlined"
          label={t('labels.bonusName')}
          error={state.name.error.length > 0}
          helperText={sentenceCase(state.name.error, true)}
          value={state.name.value}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            handleOnChange(event.target.name, event.target.value)
          }
          inputProps={{
            maxLength: 91,
          }}
        />
      </Box>
      <Box mb={2} maxWidth={400}>
        <TextField
          name="description"
          label={t('labels.bonusDescription')}
          fullWidth
          minRows={3}
          margin="dense"
          variant="outlined"
          error={state.description.error.length > 0}
          helperText={sentenceCase(state.description.error, true)}
          value={state.description.value}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            handleOnChange(event.target.name, event.target.value)
          }
          inputProps={{
            maxLength: 91,
          }}
        />
      </Box>
      <Box mb={2} maxWidth={400}>
        <TextField
          name="code"
          label={t('labels.bonusCode')}
          fullWidth
          margin="dense"
          variant="outlined"
          error={state.code.error.length > 0}
          helperText={sentenceCase(state.code.error, true)}
          value={state.code.value}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            handleOnChange(event.target.name, event.target.value)
          }
          inputProps={{
            maxLength: 91,
          }}
          disabled={
            bonusFormMode !== FormStatusType.CREATE &&
            bonusFormMode !== FormStatusType.DUPLICATE
          }
        />
      </Box>
      <Box mb={2} maxWidth={400}>
        <TextField
          name="priority"
          type="number"
          label={t('labels.priority')}
          fullWidth
          margin="dense"
          variant="outlined"
          error={state.priority.error.length > 0}
          helperText={
            state.priority.error.length > 0
              ? sentenceCase(state.priority.error, true)
              : t('priorityDescription')
          }
          value={state.priority.value}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            handleOnChange(event.target.name, event.target.value)
          }
          inputProps={{
            min: MIN_BONUS_PRIORITY,
            max: MAX_BONUS_PRIORITY,
            step: 1,
          }}
        />
      </Box>
      <Box mt={3} mb={2}>
        <Typography fontWeight={700}>{t('labels.bonusType')}</Typography>
        <FormControl margin="normal" sx={{ my: '8px' }}>
          <RadioGroup
            name="type"
            value={state.type.value}
            onChange={(event: any, newValue: any | null) => {
              handleOnChange('type', newValue)
            }}
            sx={{ display: 'flex', gap: '15px', flexDirection: 'row' }}
          >
            {bonusTypes.map((bonusType) => {
              return (
                <Box key={bonusType.index}>
                  <FormControlLabel
                    value={bonusType.index}
                    control={<Radio color="primary" />}
                    label={`${t(`bonusTypes.${bonusType.value}`)}`}
                  />
                </Box>
              )
            })}
          </RadioGroup>
        </FormControl>
      </Box>
      <BonusPeriodComponent state={state} handleOnChange={handleOnChange} />
    </>
  )
}

export default BonusDetailsComponent
